import React from 'react';
import {useWindowSize} from 'react-use';
import {RouteComponentProps, useParams} from '@reach/router';
import {Document, Image, PDFViewer, Page, StyleSheet, Text, View} from '@react-pdf/renderer';
import paystarLogo from '../assets/logo-new-two-color.png';
import {DateFormat} from '../components/date';
import {payStarColors} from '../styles/styled';
import {UsageMetricDto} from '../api/generated/index.defs';
import {first} from 'lodash';
import {AccountData} from './usage-metrics';

export const UsageMetricsPdf = (props: RouteComponentProps) => {
  const {width, height} = useWindowSize();
  const {metrics, accountData} = useParams();
  const usageData: UsageMetricDto[] = JSON.parse(atob(metrics));
  const units = first(usageData)?.unit;
  const interval = first(usageData)?.interval;
  const account = JSON.parse(atob(accountData)) as AccountData;

  return (
    <>
      {account && (
        <PDFViewer {...{height, width}}>
          <Document>
            <Page style={styles.page} size={[(width / 4) * 2.5, height]}>
              <View style={styles.titleContainer}>
                <View>
                  <Text style={styles.title}>Meter Readings</Text>
                  <Text style={styles.subTitle}>Account Name: {account.AccountName}</Text>
                  <Text style={styles.subTitle}>Account Number: {account.AccountNumber}</Text>
                  <Text style={styles.subTitle}>Service Address: {account.StreetAddress}</Text>
                  <Text style={styles.subTitle}>Units: {units}</Text>
                  <Text style={styles.subTitle}>Interval: {interval}</Text>
                </View>

                <View style={styles.logoContainer}>
                  <View style={styles.logo}>
                    <Image source={paystarLogo} />
                  </View>
                </View>
              </View>

              <View style={styles.table}>
                <View style={styles.tableRow}>
                  <View style={styles.tableColHeader}>
                    <Text style={styles.tableCellHeader}>Value {`(${units})`}</Text>
                  </View>
                  <View style={styles.tableColHeader}>
                    <Text style={styles.tableCellHeader}>Date</Text>
                  </View>
                  <View style={styles.tableColHeader}>
                    <Text style={styles.tableCellHeader}>Reading</Text>
                  </View>
                </View>

                {usageData.map((x) => (
                  <View style={styles.tableRow}>
                    <View style={styles.tableCol}>
                      <Text style={styles.tableCell}>{x.value}</Text>
                    </View>
                    <View style={styles.tableCol}>
                      <Text style={styles.tableCell}>{x.key}</Text>
                    </View>
                    <View style={styles.tableCol}>
                      <Text style={styles.tableCell}>
                        <DateFormat date={x.timestamp} format="MM/DD/YYYY" />
                      </Text>
                    </View>
                  </View>
                ))}
              </View>
            </Page>
          </Document>
        </PDFViewer>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  page: {
    padding: 20,
  },
  table: {
    width: '100%',
    paddingTop: 30,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    borderStyle: 'solid',
    borderWidth: 0,
    borderRightWidth: 0,
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableColHeader: {
    width: '33.5%', // adjust when adding or removing columns
    color: 'white',
    backgroundColor: `${payStarColors.blue3}`,
    padding: 5,
  },
  tableCol: {
    width: '33.5%', // adjust when adding or removing columns
    borderStyle: 'solid',
    borderBottomWidth: 1,
    borderColor: `${payStarColors.grey2}`,
    padding: 5,
  },
  tableCellHeader: {
    fontWeight: 'bold',
    fontSize: 12,
  },
  tableCell: {
    fontSize: 12,
  },
  logoContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    height: 50,
  },
  logo: {
    height: 175,
    width: 175,
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  title: {
    color: `${payStarColors.blue6}`,
  },
  subTitle: {
    fontSize: 12,
  },
});
